import { useCallback } from "react";
import type {
  EventTransformerHooks,
  UseInstantTransformEvent,
} from "@b2bportal/core-tracking";
import type {
  CreditHistoryModalViewedEvent,
  HotelTappedListCardEvent,
  OfferEvent,
  OffersEntryEvent,
  TappedOfferBannerEvent,
  TappedOfferModalCtaEvent,
  TappedOfferSearchEvent,
  TappedOfferTileEvent,
  TappedSeeAllHotelsEvent,
  ViewedAllOffersEvent,
  ViewedOfferBannerEvent,
  ViewedOfferDetailsModalEvent,
  ViewedOfferTileEvent,
  WalletViewedEvent,
} from "./events";
import { RbcOfferEventName } from "./events";
import type {
  CreditHistoryModalViewedRawEvent,
  HotelTappedListCardRawEvent,
  OfferRawEvent,
  OffersEntryRawEvent,
  TappedOfferBannerRawEvent,
  TappedOfferModalCtaRawEvent,
  TappedOfferSearchRawEvent,
  TappedOfferTileRawEvent,
  TappedSeeAllHotelsRawEvent,
  ViewedAllOffersRawEvent,
  ViewedOfferBannerRawEvent,
  ViewedOfferDetailsModalRawEvent,
  ViewedOfferTileRawEvent,
  WalletViewedRawEvent,
} from "./events/raw/OfferRawEvent";
import { OfferRawEventName } from "./events/raw/OfferRawEvent";

/**
 * Offer event transformers. Thin pass-throughs — global properties are attached
 * automatically by AppTrackingProvider; these map the raw name to the published
 * name and forward the properties.
 */

// Screens that already emitted viewed_offer_banner this page load, so a banner
// remount (e.g. the offer modal's hash navigate) doesn't re-count a screen.
const firedBannerScreens = new Set<string>();

export const useViewedOfferBannerTransformer: UseInstantTransformEvent<
  ViewedOfferBannerRawEvent,
  ViewedOfferBannerEvent
> = () =>
  useCallback((event) => {
    if (firedBannerScreens.has(event.properties.screen)) {
      return undefined;
    }
    firedBannerScreens.add(event.properties.screen);
    return {
      name: RbcOfferEventName.viewed_offer_banner,
      properties: { ...event.properties },
    };
  }, []);

export const useTappedOfferBannerTransformer: UseInstantTransformEvent<
  TappedOfferBannerRawEvent,
  TappedOfferBannerEvent
> = () =>
  useCallback(
    (event) => ({
      name: RbcOfferEventName.tapped_offer_banner,
      properties: { ...event.properties },
    }),
    []
  );

export const useViewedOfferTileTransformer: UseInstantTransformEvent<
  ViewedOfferTileRawEvent,
  ViewedOfferTileEvent
> = () =>
  useCallback(
    (event) => ({
      name: RbcOfferEventName.viewed_offer_tile,
      properties: { ...event.properties },
    }),
    []
  );

export const useTappedOfferTileTransformer: UseInstantTransformEvent<
  TappedOfferTileRawEvent,
  TappedOfferTileEvent
> = () =>
  useCallback(
    (event) => ({
      name: RbcOfferEventName.tapped_offer_tile,
      properties: { ...event.properties },
    }),
    []
  );

export const useViewedOfferDetailsModalTransformer: UseInstantTransformEvent<
  ViewedOfferDetailsModalRawEvent,
  ViewedOfferDetailsModalEvent
> = () =>
  useCallback(
    (event) => ({
      name: RbcOfferEventName.viewed_offer_details_modal,
      properties: { ...event.properties },
    }),
    []
  );

export const useTappedOfferModalCtaTransformer: UseInstantTransformEvent<
  TappedOfferModalCtaRawEvent,
  TappedOfferModalCtaEvent
> = () =>
  useCallback(
    (event) => ({
      name: RbcOfferEventName.tapped_offer_modal_cta,
      properties: { ...event.properties },
    }),
    []
  );

export const useOffersEntryTransformer: UseInstantTransformEvent<
  OffersEntryRawEvent,
  OffersEntryEvent
> = () =>
  useCallback(
    (event) => ({
      name: RbcOfferEventName.offers_entry,
      properties: { ...event.properties },
    }),
    []
  );

export const useTappedOfferSearchTransformer: UseInstantTransformEvent<
  TappedOfferSearchRawEvent,
  TappedOfferSearchEvent
> = () =>
  useCallback(
    (event) => ({
      name: RbcOfferEventName.tapped_offer_search,
      properties: { ...event.properties },
    }),
    []
  );

export const useHotelTappedListCardTransformer: UseInstantTransformEvent<
  HotelTappedListCardRawEvent,
  HotelTappedListCardEvent
> = () =>
  useCallback(
    (event) => ({
      name: RbcOfferEventName.hotel_tapped_list_card,
      properties: { ...event.properties },
    }),
    []
  );

export const useTappedSeeAllHotelsTransformer: UseInstantTransformEvent<
  TappedSeeAllHotelsRawEvent,
  TappedSeeAllHotelsEvent
> = () =>
  useCallback(
    (event) => ({
      name: RbcOfferEventName.tapped_see_all_hotels,
      properties: { ...event.properties },
    }),
    []
  );

export const useWalletViewedTransformer: UseInstantTransformEvent<
  WalletViewedRawEvent,
  WalletViewedEvent
> = () =>
  useCallback(
    (event) => ({
      name: RbcOfferEventName.wallet_viewed,
      properties: { ...event.properties },
    }),
    []
  );

export const useCreditHistoryModalViewedTransformer: UseInstantTransformEvent<
  CreditHistoryModalViewedRawEvent,
  CreditHistoryModalViewedEvent
> = () =>
  useCallback(
    (event) => ({
      name: RbcOfferEventName.credit_history_modal_viewed,
      properties: { ...event.properties },
    }),
    []
  );

export const useViewedAllOffersTransformer: UseInstantTransformEvent<
  ViewedAllOffersRawEvent,
  ViewedAllOffersEvent
> = () =>
  useCallback(
    (event) => ({
      name: RbcOfferEventName.viewed_all_offers,
      properties: { ...event.properties },
    }),
    []
  );

/** Transformer-hooks map for offer events, merged in AppTrackingProvider. */
export const OFFER_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  OfferRawEvent,
  OfferEvent
> = {
  [OfferRawEventName.ViewedOfferBanner]: [useViewedOfferBannerTransformer],
  [OfferRawEventName.TappedOfferBanner]: [useTappedOfferBannerTransformer],
  [OfferRawEventName.ViewedOfferTile]: [useViewedOfferTileTransformer],
  [OfferRawEventName.TappedOfferTile]: [useTappedOfferTileTransformer],
  [OfferRawEventName.ViewedOfferDetailsModal]: [
    useViewedOfferDetailsModalTransformer,
  ],
  [OfferRawEventName.TappedOfferModalCta]: [useTappedOfferModalCtaTransformer],
  [OfferRawEventName.OffersEntry]: [useOffersEntryTransformer],
  [OfferRawEventName.TappedOfferSearch]: [useTappedOfferSearchTransformer],
  [OfferRawEventName.HotelTappedListCard]: [useHotelTappedListCardTransformer],
  [OfferRawEventName.TappedSeeAllHotels]: [useTappedSeeAllHotelsTransformer],
  [OfferRawEventName.WalletViewed]: [useWalletViewedTransformer],
  [OfferRawEventName.CreditHistoryModalViewed]: [
    useCreditHistoryModalViewedTransformer,
  ],
  [OfferRawEventName.ViewedAllOffers]: [useViewedAllOffersTransformer],
};
